/**
 * OAuth Callback Handler Page - Step 2 of OAuth flow
 * Receives code from Google, retrieves invite + Turnstile from sessionStorage
 */
import { devLog, devError } from '../utils/devLog';
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Spinner, Text, VStack, useToast } from '@chakra-ui/react';
import { useAuth } from '../contexts/AuthContext';

export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { completeLogin } = useAuth();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const processingRef = useRef(false);

  // Process callback on mount
  useEffect(() => {
    // Prevent double execution in React StrictMode (dev mode)
    if (processingRef.current) {
      return;
    }
    processingRef.current = true;

    const processCallback = async () => {
      const codeParam = searchParams.get('code');
      const errorParam = searchParams.get('error');

      // Check for OAuth errors
      if (errorParam) {
        setError(`Authentication failed: ${errorParam}`);
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      if (!codeParam) {
        setError('Missing authorization code');
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      // Retrieve invite and turnstile from sessionStorage (optional for existing users)
      const inviteCode = sessionStorage.getItem('invite_code') || '';
      const turnstileToken = sessionStorage.getItem('turnstile_token') || '';

      try {
        devLog('[AuthCallback] Starting authentication with code:', codeParam.substring(0, 10) + '...');
        devLog('[AuthCallback] Has invite code:', !!inviteCode);

        // Complete authentication (invite code optional for existing users)
        await completeLogin(codeParam, inviteCode, turnstileToken);

        devLog('[AuthCallback] Authentication successful');

        // Clear sessionStorage
        sessionStorage.removeItem('invite_code');
        sessionStorage.removeItem('turnstile_token');

        toast({
          title: 'Welcome to Bhrigu Hora!',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });

        // Redirect to main app (full reload to switch from AuthApp to main App)
        window.location.href = '/';
      } catch (err: any) {
        devError('[AuthCallback] Login completion error:', err);
        setError(err.response?.data?.detail || 'Authentication failed');

        // Clear sessionStorage on error
        sessionStorage.removeItem('invite_code');
        sessionStorage.removeItem('turnstile_token');

        setTimeout(() => navigate('/login'), 3000);
      }
    };

    processCallback();

    // Cleanup function (optional, but good practice)
    return () => {
      // This runs when component unmounts or before re-running effect
    };
  }, [searchParams, navigate, completeLogin, toast]);

  // Show error state
  if (error) {
    return (
      <Box
        minH="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="gray.50"
      >
        <VStack spacing={4}>
          <Text fontSize="xl" color="red.500">
            {error}
          </Text>
          <Text color="gray.600">Redirecting to login...</Text>
        </VStack>
      </Box>
    );
  }

  // Show loading state
  return (
    <Box
      minH="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bg="gray.50"
    >
      <VStack spacing={4}>
        <Spinner size="xl" color="blue.500" thickness="4px" />
        <Text fontSize="xl" fontWeight="medium">
          Completing authentication...
        </Text>
        <Text color="gray.600">Please wait</Text>
      </VStack>
    </Box>
  );
}
